import { LocalDate } from '@js-joda/core';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import {
    BacktestStrategySettings,
    Direction,
    MarketSession,
} from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import { pick } from 'es-toolkit';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { z } from 'zod';

/**
 * Strategy-settings keys that belong to the Setup step. These are the keys
 * the Setup pane reads and writes; the Exit step owns the rest.
 */
export const SETUP_STRATEGY_KEYS = [
    'testPeriodicity',
    'entryPriceAssumption',
    'exitPriceAssumption',
    'marketsToTrade',
    'barDataCorpActions',
    'oneTradePerSymbolDay',
    'entryAllowedTimeBlocks',
    'positionCurrencySize',
    'floatMaxFraction',
    'currentVolumeMaxFraction',
    'maxNumShares',
    'applyCashCosts',
    'applyBorrowCosts',
    'applyDividends',
    'equityRate',
    'skipBelowLocatePriceFraction',
    'skipAboveLocatePriceFraction',
    'locatePriceTimeWindow',
] as const satisfies readonly (keyof BacktestStrategySettings)[];

export type SetupStrategyKey = (typeof SETUP_STRATEGY_KEYS)[number];
export type SetupStrategySettings = Pick<BacktestStrategySettings, SetupStrategyKey>;

export interface SetupStepState {
    direction: Direction;
    startDate: LocalDate | null;
    endDate: LocalDate | null;
    marketsToGenerateSignals: MarketSession[];
    strategy: SetupStrategySettings;
}

export type ValidatedSetupFields =
    | 'direction'
    | 'startDate'
    | 'endDate'
    | 'testPeriodicity'
    | 'marketsToGenerateSignals'
    | 'entryAllowedTimeBlocks'
    | 'positionCurrencySize'
    | 'floatMaxFraction'
    | 'currentVolumeMaxFraction'
    | 'maxNumShares'
    | 'entryPriceAssumption'
    | 'exitPriceAssumption';

function createInitialTouched(): Record<ValidatedSetupFields, boolean> {
    return {
        direction: false,
        startDate: false,
        endDate: false,
        testPeriodicity: false,
        marketsToGenerateSignals: false,
        entryAllowedTimeBlocks: false,
        positionCurrencySize: false,
        floatMaxFraction: false,
        currentVolumeMaxFraction: false,
        maxNumShares: false,
        entryPriceAssumption: false,
        exitPriceAssumption: false,
    };
}

export type SetupTopLevelPatch = Partial<
    Pick<SetupStepState, 'direction' | 'startDate' | 'endDate' | 'marketsToGenerateSignals'>
>;

const DEFAULT_ENTRY_WINDOW: { start: string; end: string } = { start: '09:30', end: '16:00' };

function readEntryWindow(
    blocks: BacktestStrategySettings['entryAllowedTimeBlocks'],
): { start: string; end: string } | null {
    const first = blocks?.[0]?.[0];
    if (!first) return null;
    return { start: first[0], end: first[1] };
}

interface SetupReducerState {
    form: SetupStepState;
    touched: Record<ValidatedSetupFields, boolean>;
    /**
     * UI-only memory of the most recent entry-time window, used to repopulate
     * the disabled HH:MM inputs and to restore values on toggle OFF→ON. Not
     * part of the persisted draft spec.
     */
    lastEntryWindow: { start: string; end: string };
}

type SetupAction =
    | { type: 'PATCH_FIELDS'; partial: SetupTopLevelPatch }
    | { type: 'PATCH_STRATEGY'; partial: Partial<SetupStrategySettings> }
    | { type: 'MARK_TOUCHED'; field: ValidatedSetupFields }
    | { type: 'SET_ENTRY_WINDOW_ENABLED'; enabled: boolean }
    | { type: 'RESET' };

function createInitialReducerState(): SetupReducerState {
    const form = createInitialSetupState();
    return {
        form,
        touched: createInitialTouched(),
        lastEntryWindow: readEntryWindow(form.strategy.entryAllowedTimeBlocks) ?? DEFAULT_ENTRY_WINDOW,
    };
}

/**
 * Build a fresh setup state with spec-aligned defaults. Called from the model
 * constructor and on `reset()` so dates always reflect "today" / "one month ago".
 */
export function createInitialSetupState(): SetupStepState {
    const today = LocalDate.now();
    return {
        direction: 'long',
        startDate: today.minusMonths(1),
        endDate: today,
        marketsToGenerateSignals: ['market'],
        strategy: {
            testPeriodicity: [{ value: 1, period: 'm' }],
            entryPriceAssumption: ['far-side'],
            exitPriceAssumption: ['far-side'],
            marketsToTrade: [['market']],
            barDataCorpActions: ['total-return'],
            oneTradePerSymbolDay: [false],
            entryAllowedTimeBlocks: [[['09:30', '16:00']]],
            positionCurrencySize: [10_000],
            floatMaxFraction: [0.01],
            currentVolumeMaxFraction: [0.1],
            maxNumShares: [50_000],
            applyCashCosts: [false],
            applyBorrowCosts: [false],
            applyDividends: [false],
            equityRate: [0.005],
            skipBelowLocatePriceFraction: undefined,
            skipAboveLocatePriceFraction: undefined,
            locatePriceTimeWindow: undefined,
        },
    };
}

function setupReducer(state: SetupReducerState, action: SetupAction): SetupReducerState {
    switch (action.type) {
        case 'PATCH_FIELDS':
            return { ...state, form: { ...state.form, ...action.partial } };
        case 'PATCH_STRATEGY': {
            const nextStrategy = { ...state.form.strategy, ...action.partial };
            // Mirror writes to entryAllowedTimeBlocks into UI memory so that a
            // later toggle OFF→ON restores the latest user-entered values.
            let lastEntryWindow = state.lastEntryWindow;
            if ('entryAllowedTimeBlocks' in action.partial) {
                const next = readEntryWindow(nextStrategy.entryAllowedTimeBlocks);
                if (next) lastEntryWindow = next;
            }
            return {
                ...state,
                form: { ...state.form, strategy: nextStrategy },
                lastEntryWindow,
            };
        }
        case 'MARK_TOUCHED':
            if (state.touched[action.field]) return state;
            return { ...state, touched: { ...state.touched, [action.field]: true } };
        case 'SET_ENTRY_WINDOW_ENABLED': {
            const currentWindow = readEntryWindow(state.form.strategy.entryAllowedTimeBlocks);
            const isCurrentlyEnabled = currentWindow != null;
            if (action.enabled === isCurrentlyEnabled) return state;
            if (action.enabled) {
                const { start, end } = state.lastEntryWindow;
                return {
                    ...state,
                    form: {
                        ...state.form,
                        strategy: { ...state.form.strategy, entryAllowedTimeBlocks: [[[start, end]]] },
                    },
                };
            }
            return {
                ...state,
                form: {
                    ...state.form,
                    strategy: { ...state.form.strategy, entryAllowedTimeBlocks: undefined },
                },
                lastEntryWindow: currentWindow ?? state.lastEntryWindow,
            };
        }
        case 'RESET':
            return createInitialReducerState();
        default:
            throw new UnreachableCaseError(action);
    }
}

/** Extract the Setup-owned subset from a full BacktestStrategySettings. */
export function pickSetupStrategy(strategy: BacktestStrategySettings): SetupStrategySettings {
    return pick(strategy, SETUP_STRATEGY_KEYS as unknown as SetupStrategyKey[]);
}

const requiredDate = (message: string) => z.custom<LocalDate>((v) => v instanceof LocalDate, { message });

const isNonEmptyArray = (v: unknown): boolean => Array.isArray(v) && v.length > 0;

const strategyShape = BacktestStrategySettings.shape;

const SetupTopLevelSchema = z
    .object({
        direction: Direction,
        startDate: requiredDate('Start date is required'),
        endDate: requiredDate('End date is required'),
        marketsToGenerateSignals: z.array(MarketSession).min(1, 'Trading session is required'),
    })
    .superRefine((slice, ctx) => {
        const { startDate, endDate } = slice;
        if (startDate != null && endDate != null && startDate.compareTo(endDate) > 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['startDate'],
                message: 'Start date must be before end date',
            });
        }
    });

/**
 * Per-field "non-empty array" rules are run at the object level via
 * `superRefine` rather than as `.refine()` on each optional schema. Zod skips
 * `.refine()` on optional fields whose key is absent from the input (e.g. when
 * `pickSetupStrategy` strips a key missing from a saved spec), so the field
 * rules would silently pass on legacy specs missing required fields.
 * `superRefine` always runs and reads the value via optional-chaining,
 * treating absent and `undefined` identically.
 */
const SetupStrategySchema = z
    .object({
        testPeriodicity: strategyShape.testPeriodicity,
        entryAllowedTimeBlocks: strategyShape.entryAllowedTimeBlocks,
        positionCurrencySize: strategyShape.positionCurrencySize,
        floatMaxFraction: strategyShape.floatMaxFraction,
        currentVolumeMaxFraction: strategyShape.currentVolumeMaxFraction,
        maxNumShares: strategyShape.maxNumShares,
        entryPriceAssumption: strategyShape.entryPriceAssumption,
        exitPriceAssumption: strategyShape.exitPriceAssumption,
    })
    .superRefine((data, ctx) => {
        const requireNonEmpty = (key: ValidatedSetupFields, value: unknown, message: string) => {
            if (!isNonEmptyArray(value)) {
                ctx.addIssue({ code: 'custom', path: [key], message });
            }
        };
        requireNonEmpty('testPeriodicity', data.testPeriodicity, 'Bar length is required');
        // The entry-time window is optional (toggle OFF = no constraint). When
        // present, require start < end (HH:MM strings sort lexicographically).
        const window = data.entryAllowedTimeBlocks?.[0]?.[0];
        if (window && window[0] >= window[1]) {
            ctx.addIssue({
                code: 'custom',
                path: ['entryAllowedTimeBlocks'],
                message: 'Entry start time must be before end time',
            });
        }
        requireNonEmpty('positionCurrencySize', data.positionCurrencySize, 'Position size is required');
        requireNonEmpty('floatMaxFraction', data.floatMaxFraction, 'Max % float is required');
        requireNonEmpty('currentVolumeMaxFraction', data.currentVolumeMaxFraction, 'Max % current volume is required');
        requireNonEmpty('maxNumShares', data.maxNumShares, 'Max # of shares is required');
        requireNonEmpty('entryPriceAssumption', data.entryPriceAssumption, 'Fill assumption (entry) is required');
        requireNonEmpty('exitPriceAssumption', data.exitPriceAssumption, 'Fill assumption (exit) is required');
    });

export function validateSetupState(state: SetupStepState): Record<ValidatedSetupFields, string | null> {
    const errors: Record<ValidatedSetupFields, string | null> = {
        direction: null,
        startDate: null,
        endDate: null,
        testPeriodicity: null,
        marketsToGenerateSignals: null,
        entryAllowedTimeBlocks: null,
        positionCurrencySize: null,
        floatMaxFraction: null,
        currentVolumeMaxFraction: null,
        maxNumShares: null,
        entryPriceAssumption: null,
        exitPriceAssumption: null,
    };
    const results = [
        SetupTopLevelSchema.safeParse({
            direction: state.direction,
            startDate: state.startDate,
            endDate: state.endDate,
            marketsToGenerateSignals: state.marketsToGenerateSignals,
        }),
        SetupStrategySchema.safeParse(state.strategy),
    ];
    for (const result of results) {
        if (result.success) continue;
        for (const issue of result.error.issues) {
            const key = issue.path[0];
            if (typeof key === 'string' && key in errors && errors[key as ValidatedSetupFields] == null) {
                errors[key as ValidatedSetupFields] = issue.message;
            }
        }
    }
    return errors;
}

/**
 * Setup pane signal model. Owns dates, direction, market sessions, and the
 * setup subset of strategy settings.
 */
export class SetupStepModel {
    readonly #state = signal<SetupReducerState>(createInitialReducerState());
    readonly #lastSavedJson = signal<string>(JSON.stringify(this.#state.peek().form));

    readonly state: ReadonlySignal<SetupStepState> = computed(() => this.#state.value.form);
    readonly direction: ReadonlySignal<Direction> = computed(() => this.#state.value.form.direction);
    readonly startDate: ReadonlySignal<LocalDate | null> = computed(() => this.#state.value.form.startDate);
    readonly endDate: ReadonlySignal<LocalDate | null> = computed(() => this.#state.value.form.endDate);
    readonly marketsToGenerateSignals: ReadonlySignal<MarketSession[]> = computed(
        () => this.#state.value.form.marketsToGenerateSignals,
    );
    readonly strategy: ReadonlySignal<SetupStrategySettings> = computed(() => this.#state.value.form.strategy);

    readonly fieldErrors: ReadonlySignal<Record<ValidatedSetupFields, string | null>> = computed(() =>
        validateSetupState(this.#state.value.form),
    );

    readonly validationErrors: ReadonlySignal<string[]> = computed(() =>
        Object.values(this.fieldErrors.value).filter((m): m is string => m != null),
    );
    readonly isValid: ReadonlySignal<boolean> = computed(() => this.validationErrors.value.length === 0);

    readonly isDirty: ReadonlySignal<boolean> = computed(
        () => JSON.stringify(this.#state.value.form) !== this.#lastSavedJson.value,
    );

    readonly touched: ReadonlySignal<Record<ValidatedSetupFields, boolean>> = computed(() => this.#state.value.touched);

    readonly lastEntryWindow: ReadonlySignal<{ start: string; end: string }> = computed(
        () => this.#state.value.lastEntryWindow,
    );

    markTouched(field: ValidatedSetupFields): void {
        this.#dispatch({ type: 'MARK_TOUCHED', field });
    }

    patchFields(partial: SetupTopLevelPatch): void {
        this.#dispatch({ type: 'PATCH_FIELDS', partial });
    }

    patchStrategy(partial: Partial<SetupStrategySettings>): void {
        this.#dispatch({ type: 'PATCH_STRATEGY', partial });
    }

    setEntryWindowEnabled(enabled: boolean): void {
        this.#dispatch({ type: 'SET_ENTRY_WINDOW_ENABLED', enabled });
    }

    #dispatch(action: SetupAction): void {
        this.#state.value = setupReducer(this.#state.value, action);
    }

    hydrate(form: SetupStepState): void {
        this.#state.value = {
            form,
            touched: createInitialTouched(),
            lastEntryWindow: readEntryWindow(form.strategy.entryAllowedTimeBlocks) ?? DEFAULT_ENTRY_WINDOW,
        };
        this.#lastSavedJson.value = JSON.stringify(form);
    }

    markSaved(): void {
        this.#lastSavedJson.value = JSON.stringify(this.#state.value.form);
    }

    reset(): void {
        this.#dispatch({ type: 'RESET' });
        this.#lastSavedJson.value = JSON.stringify(this.#state.peek().form);
    }
}
